import React, { FC, ReactNode } from 'react';
import { Card as AntdCard, Col, Form, InputNumber, Row, message } from 'antd';
import { Button } from 'components/UI';
import useEditActivityMutation from 'apollo/resolvers/activity/edit';
import * as texts from 'assets/texts/canopyConfiguration';
import styles from './ActivityNumberCard.module.scss';

export interface ActivityNumberCardTexts {
  title: string;
  hint?: string;
  button: string;
  success: string;
  error: string;
}

export interface ActivityNumberCardProps {
  activityId: string;
  field: 'maxStock' | 'value';
  initialValue: number;
  min: number;
  icon: ReactNode;
  thousandsSeparator?: boolean;
  texts: ActivityNumberCardTexts;
  refetch: () => Promise<unknown>;
}

const formatThousands = (value?: number | string): string =>
  `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const parseThousands = (value?: string): number => Number(value?.replace(/\./g, ''));

const ActivityNumberCard: FC<ActivityNumberCardProps> = ({
  activityId,
  field,
  initialValue,
  min,
  icon,
  thousandsSeparator,
  texts: cardTexts,
  refetch,
}) => {
  const [form] = Form.useForm();
  const [editActivityMutation, { loading }] = useEditActivityMutation();

  const update = async (values: Record<typeof field, number>) => {
    try {
      await editActivityMutation({
        variables: { input: { id: activityId, [field]: values[field] } },
      });
      await refetch();
      await message.success(cardTexts.success);
    } catch {
      await refetch();
      await message.error(cardTexts.error);
    }
  };

  return (
    <AntdCard>
      <p>{cardTexts.title}</p>
      <Form
        form={form}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onFinish={update}
      >
        <Row justify="start" align="middle">
          <Col span={16}>
            <Form.Item
              name={field}
              initialValue={initialValue}
              className={styles.FormItem}
              rules={[
                { required: true, message: texts.requiredField },
                { type: 'number', min },
              ]}
              extra={cardTexts.hint}
            >
              <InputNumber
                className={styles.Input}
                controls={false}
                min={min}
                precision={0}
                formatter={thousandsSeparator ? formatThousands : undefined}
                parser={thousandsSeparator ? parseThousands : undefined}
                prefix={<span className={styles.SmallIcon}>{icon}</span>}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Col span={24}>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                text={cardTexts.button}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </AntdCard>
  );
};

ActivityNumberCard.defaultProps = {
  thousandsSeparator: false,
};

export default ActivityNumberCard;
